/**
 * The Nutrition V2 palette, from the design mock (`מסך תזונה אדמין.html`).
 *
 * Why V2 keeps its own tokens instead of extending `coachTokens`: `t` has ~170 importers
 * across the whole admin, so tuning an ink or a shadow there would silently restyle the
 * dashboard, the client pages and the food dictionary. `ui/macroTokens.ts` already set this
 * precedent for the macro hues; this is the same idea for the neutrals.
 *
 * ── the two things that make this palette read differently from the rest of the admin ──
 *
 * FLAT CARDS. `shadow` is two 1px layers. `t.sh` spreads 20px, which makes a card float; the
 * mock's cards sit on the page. On a builder screen that is mostly cards inside cards, the
 * floating version reads as clutter.
 *
 * FOUR INKS, NOT THREE. `t3` in coachTokens (#a0aac0) is light enough that secondary text
 * reads as disabled. The mock splits that step: `ink3` for text you are meant to read and
 * `ink4` for text that is genuinely incidental.
 *
 * The BRAND is deliberately NOT the mock's. See `brand` below.
 */

import { t } from "../../../dashboard/coach/coachTokens";

export const v2 = {
  /* surfaces */
  canvas: "#F6F8FA",
  surface: "#FFFFFF",
  /** row and card hover — a hair off the canvas, not a grey block */
  hover: "#F7F9FB",

  /* lines */
  line: "#E7EBEF",
  lineSoft: "#F0F3F6",

  /* ink, darkest to lightest */
  ink: "#141C24",
  ink2: "#4A5560",
  ink3: "#7D8894",
  ink4: "#A9B2BC",

  /**
   * The brand green stays the APP's, not the mock's.
   *
   * The mock writes `#07BBA6`, which in this codebase is the *calories* macro hue
   * (`theme.palette.primary.calories`) rather than the brand — the brand is `#01BBA7`. The two
   * are six units apart and indistinguishable side by side, so matching the mock here would
   * fork the brand colour away from every other admin screen to buy nothing.
   */
  brand: t.green,
  brandDark: t.greenDark,
  brandWash: "#EAF9F7",
  brandText: t.greenText,

  /* status. `warn` is "you should look at this" (unsaved changes, off the calculator's
     recommendation); `over` is "this number is past its target". */
  warn: "#B4700A",
  warnWash: "#FEF6E7",
  over: "#D6483B",
  overWash: "#FDF0EF",

  /**
   * "Short of the target" — a fourth status, and the first that is not a warning.
   *
   * Nothing needed it until the food log's month calendar, because everywhere else falling short is
   * a COUNTDOWN (`נותרו 640`) rather than a verdict. A finished day is different: under-eating is a
   * finding a trainer wants to see, so it gets a colour — a cool, low-saturation one, because it is
   * information rather than an alarm.
   *
   * Deliberately darker and flatter than the protein hue (`#4B7BEC`): the calendar's metric switcher
   * can be set to חלבון, at which point protein-blue numbers sit inside under-blue cells, and the two
   * blues have to stay tellable apart.
   */
  under: "#3B6EA5",
  underWash: "#EDF3F9",

  /* radii */
  rSm: "8px",
  r: "12px",
  rLg: "16px",

  /**
   * The day's calories cut up by meal.
   *
   * One quantity split into parts, so it is the brand stepped down rather than a categorical
   * palette — meals are not five unrelated categories, they are five slices of one day. Wrap with
   * `brandRamp[i % brandRamp.length]` for a day with more meals than steps.
   */
  brandRamp: ["#01BBA7", "#3FCCBC", "#6ED9CC", "#98E4DB", "#BFEEE9", "#DDF6F3"],

  shadow: "0 1px 2px rgba(20,28,36,.04), 0 1px 3px rgba(20,28,36,.05)",
  shadowMenu: "0 4px 6px -2px rgba(20,28,36,.06), 0 12px 24px -6px rgba(20,28,36,.12)",
  shadowDialog: "0 24px 60px -12px rgba(20,28,36,.35)",
};

/**
 * Spread into any `sx` carrying a figure that changes as the trainer types.
 *
 * Without it, a column of macros re-flows on every keystroke because Heebo's digits are
 * proportional — "111" is narrower than "999". Everywhere a number sits in a column, a table
 * or next to a bar, it wants this.
 */
export const nums = { fontVariantNumeric: "tabular-nums" } as const;

/**
 * Reveal a row's controls on hover or keyboard focus.
 *
 * Spread `hoverReveal.target` onto the row and `hoverReveal.item` onto the controls.
 *
 * Deliberately `opacity` and NOT `display: none` / `visibility: hidden`: those take the
 * controls out of the tab order, which would leave a keyboard user unable to reorder or delete
 * a food at all. `:focus-within` on the row is what makes tabbing into a hidden control show
 * it.
 */
export const hoverReveal = {
  target: {
    "&:hover .nv2-reveal, &:focus-within .nv2-reveal": { opacity: 1 },
  },
  item: {
    /**
     * Quiet until you reach for the row — but only where there is something to reach WITH.
     *
     * A finger cannot hover, so on a touch device `opacity: 0` was not "quiet", it was
     * "invisible, permanently": the reorder arrows on every food row simply could not be found.
     * Where the pointer cannot hover, the controls are always shown.
     *
     * `hover: none` rather than `pointer: coarse` — a stylus is coarse and hovers perfectly well,
     * and it is hovering, not precision, that this rule depends on. The `xs` value carries the
     * same behaviour to a narrow desktop window, so the phone layout can be checked by resizing
     * without also emulating touch.
     */
    opacity: { xs: 1, md: 0 },
    transition: "opacity .12s",
    "@media (hover: none)": { opacity: 1 },
  },
  /** the class the two halves agree on */
  className: "nv2-reveal",
};

/**
 * The floor for a control that is tapped rather than clicked.
 *
 * 40px, not the iOS HIG's 44: these controls sit in clusters inside a row that is itself a much
 * larger target, and at 44 a food row's three icons would be taller than the food's own name. 44
 * is right for a control standing alone — the save button, a dialog's actions — and those set
 * their own height.
 *
 * Desktop keeps whatever size the control already had; `0` here means "no floor", not "zero".
 */
export const tapTarget = {
  minWidth: { xs: "40px", md: 0 },
  minHeight: { xs: "40px", md: 0 },
} as const;
